import React, { useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import styles from './ReadingViewAll.module.css';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { useNavigate, useParams } from 'react-router-dom';
import Loading from '../../../common/HousGlass Loader/Loading';
import { useEbooksByGenreName } from '../../../hooks/useEbooksHooks';
import Footer from '../../../common/UI/Footer';

function ReadingViewAll() {
  const { param } = useParams();
  const { 
    ebooks,
    isLoading,
    error
  } = useEbooksByGenreName(param);
  
  const navigate = useNavigate();
  
  // Extract the actual books data from the response
  const filteredBooks = useMemo(() => {
    if (!ebooks) return [];
    return ebooks || [];
  }, [ebooks]);

  // Get top rated books
  const topBooks = useMemo(() => {
    if (!filteredBooks.length) return [];
    
    return [...filteredBooks]
      .sort((a, b) => b.likes_count - a.likes_count)
      .slice(0, 5);
  }, [filteredBooks]);
  
  // Handle navigation to book details
  const handleBookClick = (book) => {
    // If there's a series_slug, navigate to that, otherwise navigate to the book's slug
    if(book.series_id !== null) {
      const navigateSlug = book.slug;
      navigate(`/book-details/${navigateSlug}`);
    } else {
      navigate(`/book-detail/${book.slug}`);
    }
  };

  // Loading states
  if (isLoading) {
    return <div className={styles.loading}><Loading/></div>;
  }

  // Error states
  if (error) {
    return <div className={styles.error}>Error loading content. Please try again.</div>;
  }

  // No books found
  if (!filteredBooks.length) {
    return <div className={styles.noBooks}>No books found for genre: {param}</div>;
  }
  
  return (
    <>
    <div className={styles.container}>
      {/* Showcase Section */}
      <section className={styles.showcase}>
        <Swiper
          modules={[Navigation, Pagination]}
          spaceBetween={20}
          navigation
          pagination={{ clickable: true }}
          breakpoints={{
            320: { slidesPerView: 1 },
            640: { slidesPerView: 1 },
            968: { slidesPerView: 1 },
            1200: { slidesPerView: 1 }
          }}
          className={styles.showcaseSwiper}
        >
          {topBooks.map((book) => (
            <SwiperSlide key={book.id} onClick={() => handleBookClick(book)}>
              <div className={styles.showcaseSlide}>
                <div
                  className={styles.backgroundImage}
                  style={{ backgroundImage: `url(${book.cover_image_url_for_computer})` }}
                />
                <div className={styles.contentWrapper}>
                  <div className={styles.bookDetails}>
                    <h1 className={styles.title}>{book.title}</h1>
                    <p className={styles.author}>By {book.author}</p>
                    <p className={styles.category}>{book.genre_name}</p>
                  </div>
                  <div className={styles.coverImageContainer}>
                    <img
                      src={book.cover_image_url_for_computer}
                      alt={book.title}
                      className={styles.coverImage}
                    />
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </section>

      {/* Book Grid */}
      <section className={styles.booksGrid}>
        <h2 className={styles.gridTitle}>All {param} Books</h2>
        <div className={styles.grid}>
          {filteredBooks.map((book) => (
            <div 
              key={book.id} 
              className={styles.bookCard} 
              onClick={() => handleBookClick(book)}
            >
              <div className={styles.imageContainer}>
                <img src={book.cover_image_url_for_computer} alt={book.title} />
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
    <Footer/>
    </>
  );
}

export default ReadingViewAll;